import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIos";
import axios from "axios";
import apilink from "../globals/Baseurls";
import { useNavigate } from "react-router-dom";

export default function PhotoUpload() {
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(null); // for new uploads
  const [primaryExistingId, setPrimaryExistingId] = useState(null);

  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("userSessionData"));
  const token = userData?.token;

  // 🔄 Load existing photos
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await axios.get(`${apilink.link}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const photos = res.data.photos || [];
        setExistingPhotos(photos);

        const primary = photos.find((p) => p.is_primary);
        if (primary) setPrimaryExistingId(primary.id);
      } catch (err) {
        console.error(err.response?.data || err.message);
      }
    };

    fetchPhotos();
  }, []);

  // ➕ select new files
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  // ❌ remove new file
  const handleRemoveNew = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (primaryIndex === index) setPrimaryIndex(null);
  };

  // 🗑 delete existing photo
  const handleDeleteExisting = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;

    try {
      await axios.delete(`${apilink.link}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));

      if (primaryExistingId === photoId) {
        setPrimaryExistingId(null);
      }
    } catch (err) {
      alert("Failed to delete photo");
    }
  };

  // ⭐ make existing primary
  const makeExistingPrimary = async (photoId) => {
    try {
      await axios.post(
        `${apilink.link}/photos/${photoId}/make-primary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setExistingPhotos((prev) =>
        prev.map((p) => ({
          ...p,
          is_primary: p.id === photoId,
        }))
      );
      setPrimaryExistingId(photoId);
      setPrimaryIndex(null); // unset new primary
    } catch {
      alert("Failed to set primary");
    }
  };

  // ♻ clear new
  const handleClearAll = () => {
    setSelectedFiles([]);
    setPrimaryIndex(null);
  };

  // ⬆ upload new photos
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    selectedFiles.forEach((file, i) => {
      formData.append("photos[]", file);
      formData.append("is_primary[]", i === primaryIndex ? 1 : 0);
    });

    try {
      const res = await axios.post(`${apilink.link}/photos`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Photos uploaded!");
      setSelectedFiles([]);
      setPrimaryIndex(null);

      // refresh photos
      setExistingPhotos((prev) => [...prev, ...res.data.photos]);
    } catch (err) {
      alert("Upload failed");
    }
  };

  return (
    <Box p={2} maxWidth={500} mx="auto">
      {/* 🔙 Top bar */}
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton
          onClick={() => navigate("/profile")}
          sx={{
            bgcolor: "#f0f0f0",       // faint gray
            width: 40,                 // fixed width
            height: 40,                // fixed height
            borderRadius: "50%",       // perfect circle
            display: "flex",
            alignItems: "center",      // vertical center
            justifyContent: "center",  // horizontal center
            "&:hover": {
              bgcolor: "#e0e0e0",
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Typography variant="h6" ml={1}>
          Edit Photos
        </Typography>
      </Box>

      {/* 📸 Existing photos */}
      <Typography variant="subtitle1" mb={1}>
        Your Photos
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        {existingPhotos.map((p) => (
          <Card key={p.id} sx={{ width: 120, position: "relative" }}>
            <CardMedia
              component="img"
              image={`${apilink.base_link}/storage/${p.path}`}
              sx={{ height: 120, objectFit: "cover" }}
            />

            {/* delete */}
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                bgcolor: "rgba(255,255,255,0.85)",
              }}
              onClick={() => handleDeleteExisting(p.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            {/* primary */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={p.is_primary}
                  onChange={() => makeExistingPrimary(p.id)}
                />
              }
              label="Primary"
              sx={{
                position: "absolute",
                bottom: 2,
                left: 2,
                bgcolor: "rgba(255,255,255,0.85)",
                borderRadius: 1,
                px: 0.5,
              }}
            />
          </Card>
        ))}
      </Box>

      {/* ➕ Controls */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button variant="contained" component="label">
          Select Photos
          <input hidden multiple type="file" accept="image/*" onChange={handleFileChange} />
        </Button>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={selectedFiles.length === 0}
        >
          Upload
        </Button>

        <Button
          variant="outlined"
          onClick={handleClearAll}
          sx={{ borderColor: "red", color: "red" }}
        >
          Clear
        </Button>
      </Box>

      {/* 🆕 New previews */}
      <Typography variant="subtitle1" mb={1}>
        New Photos
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {selectedFiles.map((file, index) => (
          <Card key={index} sx={{ width: 120, position: "relative" }}>
            <CardMedia
              component="img"
              image={URL.createObjectURL(file)}
              sx={{ height: 120, objectFit: "cover" }}
            />

            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                bgcolor: "rgba(255,255,255,0.85)",
              }}
              onClick={() => handleRemoveNew(index)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            <FormControlLabel
              control={
                <Checkbox
                  checked={primaryIndex === index}
                  onChange={() => {
                    setPrimaryIndex(index);
                    setPrimaryExistingId(null);
                  }}
                />
              }
              label="Primary"
              sx={{
                position: "absolute",
                bottom: 2,
                left: 2,
                bgcolor: "rgba(255,255,255,0.85)",
                borderRadius: 1,
                px: 0.5,
              }}
            />
          </Card>
        ))}
      </Box>
    </Box>
  );
}
